'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import Link from 'next/link'
import Image from 'next/image'

// Animated broken connections
function BrokenConnections() {
  const [nodes] = useState(() =>
    Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: 20 + Math.random() * 60,
      y: 10 + Math.random() * 80,
      size: 4 + Math.random() * 6,
    }))
  )

  return (
    <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 100 100" preserveAspectRatio="none">
      {/* Broken connection lines */}
      {nodes.map((node, i) => {
        const next = nodes[(i + 1) % nodes.length]
        const midX = (node.x + next.x) / 2
        const midY = (node.y + next.y) / 2
        return (
          <g key={`line-${i}`}>
            {/* Line from node to midpoint (broken) */}
            <motion.line
              x1={node.x}
              y1={node.y}
              x2={midX}
              y2={midY}
              stroke="#ff5f57"
              strokeWidth="0.3"
              strokeDasharray="2 2"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: [0, 1, 0] }}
              transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: 'easeInOut' }}
            />
          </g>
        )
      })}
      {/* Nodes */}
      {nodes.map(node => (
        <motion.circle
          key={`node-${node.id}`}
          cx={node.x}
          cy={node.y}
          r={node.size / 10}
          fill="#ff5f57"
          animate={{ opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 2 + Math.random() * 2, repeat: Infinity }}
        />
      ))}
    </svg>
  )
}

export default function ErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [countdown, setCountdown] = useState(15)

  useEffect(() => {
    console.error('Application error:', error)
  }, [error])

  // Auto-retry countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer)
          reset()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [reset])

  return (
    <div className="min-h-screen bg-[#1d212a] relative overflow-hidden flex items-center justify-center">
      <BrokenConnections />

      {/* Pulsing warning glow */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(255,95,87,0.08) 0%, transparent 70%)' }}
        animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative z-10 container max-w-3xl mx-auto px-6 text-center">
        <Link href="/" className="inline-block mb-12">
          <Image
            src="/hmn_logo.png"
            alt="hmn"
            width={140}
            height={30}
            className="w-[100px] opacity-60 hover:opacity-100 transition-opacity mx-auto"
          />
        </Link>

        {/* Error icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', damping: 15 }}
          className="mx-auto mb-8 w-24 h-24 rounded-full bg-[#ff5f57]/10 border border-[#ff5f57]/30 flex items-center justify-center"
        >
          <motion.span
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 3 }}
            className="text-5xl select-none"
          >
            ⚡
          </motion.span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-3xl md:text-[48px] font-normal text-white leading-[1.1] font-gendy mb-4"
        >
          Something broke.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="text-white text-xl md:text-[26px] leading-[1.4] font-anuphan mb-3"
        >
          The kind of thing we help organizations prevent.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-[#8a8f9e] text-lg font-anuphan mb-10 max-w-[500px] mx-auto"
        >
          Our systems hit an unexpected error. We&apos;ve been notified and are looking into it.
        </motion.p>

        {/* Auto-retry indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mb-10"
        >
          <div className="inline-flex items-center gap-3 bg-white/[0.03] border border-[#3e4658] rounded-full px-6 py-3">
            <div className="relative w-8 h-8">
              <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
                <circle cx="16" cy="16" r="14" fill="none" stroke="#3e4658" strokeWidth="2" />
                <motion.circle
                  cx="16"
                  cy="16"
                  r="14"
                  fill="none"
                  stroke="#61d8fe"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 14}
                  animate={{ strokeDashoffset: [2 * Math.PI * 14, 0] }}
                  transition={{ duration: 15, ease: 'linear' }}
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[#61d8fe] text-xs font-anuphan font-bold">
                {countdown}
              </span>
            </div>
            <span className="text-[#8a8f9e] text-sm font-anuphan">Auto-retrying...</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <motion.button
            onClick={reset}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-[#544ae8] border border-[#475065] rounded-[100px] px-8 py-4 text-white text-lg font-anuphan"
          >
            Try Again Now
          </motion.button>
          <Link href="/">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="border border-white/20 rounded-[100px] px-8 py-4 text-white text-lg font-anuphan hover:bg-white/5 transition-colors"
            >
              Go Home
            </motion.button>
          </Link>
        </motion.div>

        {/* Error digest for debugging */}
        {error.digest && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-10 text-[#3e4658] text-xs font-mono"
          >
            Error ID: {error.digest}
          </motion.p>
        )}
      </div>
    </div>
  )
}
